import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { orderLineItemId, type OrderLineItemId } from './OrderLineItem.js'
import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.service_item_ */
export type ServiceItemId = string & { __brand: 'ServiceItemId' }

/** Represents the table public.service_item_ */
export default interface ServiceItemTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<ServiceItemId, ServiceItemId | undefined, ServiceItemId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  orderLineItemId: ColumnType<
    OrderLineItemId | null,
    OrderLineItemId | null,
    OrderLineItemId | null
  >

  /** Database type: pg_catalog.numeric */
  amount: ColumnType<number, number, number>

  /** Database type: pg_catalog.timestamptz */
  startedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  expiredAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.int4 */
  daysPurchased: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  daysConsumed: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<
    OrganizationId | null,
    OrganizationId | null,
    OrganizationId | null
  >

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId | null, ProjectId | null, ProjectId | null>

  /** Database type: pg_catalog.uuid */
  nextServiceItemId: ColumnType<
    ServiceItemId | null,
    ServiceItemId | null,
    ServiceItemId | null
  >

  /** Database type: pg_catalog.text */
  externalReference: ColumnType<string | null, string | null, string | null>
}

export type ServiceItem = Selectable<ServiceItemTable>

export type ServiceItemInitializer = Insertable<ServiceItemTable>

export type ServiceItemMutator = Updateable<ServiceItemTable>

export const serviceItemId = z.string() as unknown as z.Schema<ServiceItemId>

export const serviceItem = z.object({
  id: serviceItemId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  orderLineItemId: orderLineItemId.nullable().optional(),
  amount: z.number(),
  startedAt: z.coerce.date().nullable().optional(),
  expiredAt: z.coerce.date().nullable().optional(),
  daysPurchased: z.number().nullable().optional(),
  daysConsumed: z.number().nullable().optional(),
  organizationId: organizationId.nullable().optional(),
  projectId: projectId.nullable().optional(),
  nextServiceItemId: serviceItemId.nullable().optional(),
  externalReference: z.string().nullable().optional(),
}) as unknown as z.Schema<ServiceItem>

export const serviceItemInitializer = z.object({
  id: serviceItemId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  orderLineItemId: orderLineItemId.optional().nullable(),
  amount: z.number(),
  startedAt: z.coerce.date().optional().nullable(),
  expiredAt: z.coerce.date().optional().nullable(),
  daysPurchased: z.number().optional().nullable(),
  daysConsumed: z.number().optional().nullable(),
  organizationId: organizationId.optional().nullable(),
  projectId: projectId.optional().nullable(),
  nextServiceItemId: serviceItemId.optional().nullable(),
  externalReference: z.string().optional().nullable(),
}) as unknown as z.Schema<ServiceItemInitializer>

export const serviceItemMutator = z.object({
  id: serviceItemId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  orderLineItemId: orderLineItemId.optional().nullable(),
  amount: z.number().optional(),
  startedAt: z.coerce.date().optional().nullable(),
  expiredAt: z.coerce.date().optional().nullable(),
  daysPurchased: z.number().optional().nullable(),
  daysConsumed: z.number().optional().nullable(),
  organizationId: organizationId.optional().nullable(),
  projectId: projectId.optional().nullable(),
  nextServiceItemId: serviceItemId.optional().nullable(),
  externalReference: z.string().optional().nullable(),
}) as unknown as z.Schema<ServiceItemMutator>
